import { motion, useReducedMotion } from "framer-motion";
import { easeOutSoft } from "@/lib/motion";

/* ================================================================== *
 * <IsometricBlockModel>                                               *
 *                                                                     *
 * A true isometric-style 3D block model rendered in SVG. Blocks       *
 * assemble themselves diagonally (as if being kriged left→right,      *
 * bottom→top), each with a top face, front face and side face for a  *
 * genuine "3D voxel" feel. Grade drives the top-face colour.          *
 * ================================================================== */
export function IsometricBlockModel() {
  const reduce = useReducedMotion();

  // Isometric parameters — 30° projection.
  const bw = 30;                        // block face size
  const dx = bw * Math.cos(Math.PI / 6); // ≈ 26
  const dy = bw * Math.sin(Math.PI / 6); // ≈ 15
  const bh = 18;                        // block "height"

  const cols = 7;
  const rows = 5;
  const originX = 200;
  const originY = 200;

  // Grade function centred on a diagonal "vein".
  const grade = (c, r) => {
    const dxg = c - 3.5, dyg = r - 2;
    const d = Math.sqrt(dxg * dxg * 0.55 + dyg * dyg);
    return Math.max(0, 1 - d / 4);
  };

  const gradeColour = (g) => {
    if (g > 0.72) return { top: "#F59E0B", front: "#B4740A", side: "#8B5A08" };
    if (g > 0.48) return { top: "#22C7B0", front: "#159780", side: "#0F6E5D" };
    if (g > 0.28) return { top: "#3B82F6", front: "#1E5EDB", side: "#1848A8" };
    return { top: "#1E6FFF", front: "#1552C7", side: "#0F3D97" };
  };

  const blocks = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const g = grade(c, r);
      const col = gradeColour(g);

      // Isometric coordinates for the block's bottom-front corner.
      const bx = originX + (c - r) * dx;
      const by = originY + (c + r) * dy;

      // Reveal order: back-to-front, left-to-right (diagonal wave).
      const order = c + r;
      const delay = 0.15 + order * 0.055;

      blocks.push(
        <motion.g
          key={`${r}-${c}`}
          initial={reduce ? false : { opacity: 0, y: 20 }}
          animate={reduce ? false : { opacity: 1, y: 0 }}
          transition={reduce ? undefined : { duration: 0.55, ease: easeOutSoft, delay }}
        >
          {/* Front face (parallelogram) */}
          <path
            d={`M ${bx} ${by} L ${bx + dx} ${by + dy} L ${bx + dx} ${by + dy + bh} L ${bx} ${by + bh} Z`}
            fill={col.front}
            opacity="0.9"
          />
          {/* Side face */}
          <path
            d={`M ${bx + dx} ${by + dy} L ${bx + dx + dx} ${by + dy - dy} L ${bx + dx + dx} ${by + dy - dy + bh} L ${bx + dx} ${by + dy + bh} Z`}
            fill={col.side}
            opacity="0.9"
          />
          {/* Top face */}
          <path
            d={`M ${bx} ${by} L ${bx + dx} ${by + dy} L ${bx + dx + dx} ${by + dy - dy} L ${bx + dx} ${by - dy} Z`}
            fill={col.top}
            opacity="0.95"
          />
          {/* Subtle top-face inner outline for definition */}
          <path
            d={`M ${bx} ${by} L ${bx + dx} ${by + dy} L ${bx + dx + dx} ${by + dy - dy} L ${bx + dx} ${by - dy} Z`}
            fill="none"
            stroke="rgba(255,255,255,0.35)"
            strokeWidth="0.6"
          />
        </motion.g>
      );
    }
  }

  return (
    <svg viewBox="0 0 500 380" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="iso-glow" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#1E6FFF" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#1E6FFF" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* Soft glow beneath the model */}
      <ellipse cx="270" cy="330" rx="200" ry="30" fill="url(#iso-glow)" />
      {blocks}
    </svg>
  );
}

/* ================================================================== *
 * <DrillholeDescent>                                                  *
 *                                                                     *
 * A vertical drillhole descending through stratigraphy — the drill    *
 * bit slides down, revealing coloured lithological intervals along    *
 * the way. Used as a decorative accent in the hero.                   *
 * ================================================================== */
export function DrillholeDescent() {
  const reduce = useReducedMotion();
  // Lithology intervals top→bottom.
  const intervals = [
    { from: 20,  to: 60,  fill: "#D4A574", label: "OVB" },
    { from: 60,  to: 120, fill: "#8B6F47", label: "WX" },
    { from: 120, to: 210, fill: "#1E6FFF", label: "ORE" },
    { from: 210, to: 260, fill: "#22C7B0", label: "HG" },
    { from: 260, to: 340, fill: "#475569", label: "BIF" },
    { from: 340, to: 400, fill: "#334155", label: "GN" },
  ];

  return (
    <svg viewBox="0 0 130 420" className="w-full h-full" preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="dh-earth" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#F8FAFC" />
          <stop offset="100%" stopColor="#E2E8F0" />
        </linearGradient>
      </defs>
      {/* Surface + earth column background */}
      <rect x="24" y="0" width="82" height="20" fill="#94A3B8" opacity="0.18" />
      <rect x="24" y="20" width="82" height="380" fill="url(#dh-earth)" />
      <line x1="24" y1="20" x2="106" y2="20" stroke="#94A3B8" strokeWidth="0.6" strokeDasharray="2 2" />

      {/* Depth ticks */}
      {[100, 200, 300, 400].map((d) => (
        <g key={d}>
          <line x1="22" y1={d} x2="26" y2={d} stroke="#64748B" strokeWidth="1" />
          <text x="18" y={d + 3} fontSize="7" fill="#64748B" textAnchor="end" fontFamily="monospace">{d - 20}m</text>
        </g>
      ))}

      {/* Lithology intervals reveal top→bottom */}
      {intervals.map((iv, i) => (
        <motion.g
          key={i}
          initial={reduce ? false : { opacity: 0, scaleY: 0 }}
          animate={reduce ? false : { opacity: 1, scaleY: 1 }}
          transition={reduce ? undefined : {
            duration: 0.5, ease: easeOutSoft, delay: 0.6 + i * 0.25,
          }}
          style={{ transformOrigin: `65px ${iv.from}px` }}
        >
          <rect x="56" y={iv.from} width="18" height={iv.to - iv.from} fill={iv.fill} opacity="0.92" />
          <text x="82" y={(iv.from + iv.to) / 2 + 3} fontSize="9" fontFamily="monospace" fill="#0F172A" fontWeight="700">
            {iv.label}
          </text>
        </motion.g>
      ))}

      {/* Drill bit descent */}
      <motion.g
        initial={reduce ? false : { y: -20, opacity: 0 }}
        animate={reduce ? false : { y: 380, opacity: 1 }}
        transition={reduce ? undefined : {
          y: { duration: 2.6, ease: [0.6, 0, 0.4, 1] },
          opacity: { duration: 0.3 },
        }}
      >
        <rect x="60" y="0" width="10" height="16" fill="#0F172A" />
        <path d="M 60 16 L 70 16 L 65 26 Z" fill="#F59E0B" />
      </motion.g>

      {/* Central drillhole trace line */}
      <motion.line
        x1="65" y1="0" x2="65" y2="410"
        stroke="#0F172A" strokeWidth="0.6" strokeDasharray="2 3"
        initial={reduce ? false : { pathLength: 0 }}
        animate={reduce ? false : { pathLength: 1 }}
        transition={reduce ? undefined : { duration: 2.6, ease: [0.6, 0, 0.4, 1] }}
      />
    </svg>
  );
}

/* ================================================================== *
 * <CommodityOrbit>                                                    *
 *                                                                     *
 * A gentle orbital arrangement of the commodities SRES estimates.     *
 * Pills rotate slowly around a central "resource" node. Purely        *
 * decorative but reinforces the "multi-commodity" positioning.        *
 * ================================================================== */
export function CommodityOrbit() {
  const reduce = useReducedMotion();
  const commodities = ["Fe", "Au", "Cu", "Ag", "Ni", "U₃O₈", "Li", "Zn"];

  return (
    <div className="relative w-full aspect-square max-w-sm mx-auto">
      {/* Central node */}
      <div className="absolute inset-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-24 rounded-full bg-slate-900 text-white flex items-center justify-center font-heading font-black text-sm tracking-widest border-4 border-white shadow-[0_10px_30px_-12px_rgba(15,23,42,0.35)]">
        SRES
      </div>
      {/* Orbit ring */}
      <div className="absolute inset-4 rounded-full border border-dashed border-slate-300" />

      {/* Commodity pills — static positions with gentle fade-in */}
      <div className="absolute inset-0">
        {commodities.map((c, i) => {
          const angle = (i * 360) / commodities.length;
          return (
            <div
              key={c}
              className="absolute left-1/2 top-1/2"
              style={{ transform: `rotate(${angle}deg) translateX(140px) rotate(-${angle}deg)` }}
            >
              <motion.div
                className="-translate-x-1/2 -translate-y-1/2 inline-flex items-center justify-center min-w-[52px] h-9 px-3 rounded-md bg-white border border-slate-200 shadow-sm text-slate-800 font-mono text-[12px] font-semibold"
                initial={reduce ? false : { opacity: 0, scale: 0.85 }}
                whileInView={reduce ? false : { opacity: 1, scale: 1 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={reduce ? undefined : { duration: 0.45, ease: easeOutSoft, delay: 0.1 + i * 0.05 }}
              >
                {c}
              </motion.div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ================================================================== *
 * <LogosMarquee>                                                      *
 *                                                                     *
 * A subtle horizontal scrolling strip of "trusted by" logos           *
 * expressed as monospaced text blocks — since we don't want to fake   *
 * real client logos, we surface the reporting standards + geosciences *
 * stacks SRES ships with.                                             *
 * ================================================================== */
export function StandardsMarquee() {
  const items = [
    "JORC · 2012",
    "NI 43-101",
    "SAMREC · 2016",
    "CIM · Best Practice",
    "PERC · 2021",
    "ISO 9001-ready",
    "SOC 2 · roadmap",
  ];

  return (
    <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
      {items.map((item) => (
        <div key={item} className="flex items-center gap-2.5 text-slate-500 text-[11.5px] font-mono uppercase tracking-[0.24em]">
          <span className="w-1 h-1 rounded-full bg-brand-blue" />
          {item}
        </div>
      ))}
    </div>
  );
}
